import React, { useMemo, useState } from 'react'
import { updateDeal } from 'submission/lib/deal-api'
import useToast from 'hooks/useToast'

type Props = {
  deal: any
  onSaved: (deal: any) => void
}

const toLocalInputValue = (iso?: string | null): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`
}

const fromLocalInputValue = (val: string): string | null => {
  if (!val) return null
  const d = new Date(val)
  return isNaN(d.getTime()) ? null : d.toISOString()
}

const Settings: React.FC<Props> = ({ deal, onSaved }) => {
  const { showToast } = useToast()

  const initial = useMemo(
    () => ({
      max_investors:
        deal?.max_investors == null ? '' : String(deal.max_investors),
      shortlist_deadline_hours:
        deal?.shortlist_deadline_hours == null
          ? ''
          : String(deal.shortlist_deadline_hours),
      shortlist_absolute_deadline: toLocalInputValue(
        deal?.shortlist_absolute_deadline,
      ),
    }),
    [deal],
  )

  const [maxInvestors, setMaxInvestors] = useState(initial.max_investors)
  const [deadlineHours, setDeadlineHours] = useState(
    initial.shortlist_deadline_hours,
  )
  const [absoluteDeadline, setAbsoluteDeadline] = useState(
    initial.shortlist_absolute_deadline,
  )
  const [saving, setSaving] = useState(false)

  const dirty =
    maxInvestors !== initial.max_investors ||
    deadlineHours !== initial.shortlist_deadline_hours ||
    absoluteDeadline !== initial.shortlist_absolute_deadline

  const onSave = async () => {
    setSaving(true)
    try {
      const merged = {
        ...deal,
        max_investors: maxInvestors === '' ? null : Number(maxInvestors),
        shortlist_deadline_hours:
          deadlineHours === '' ? null : Number(deadlineHours),
        shortlist_absolute_deadline: fromLocalInputValue(absoluteDeadline),
      }
      const res = await updateDeal(String(deal.id), merged)
      onSaved(res?.data ?? merged)
      showToast({
        type: 'success',
        message: 'Shortlist settings saved',
      })
    } catch (e: any) {
      showToast({
        type: 'error',
        message: `Failed to save settings: ${e?.message ?? 'unknown error'}`,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <section
      data-testid="shortlist-settings"
      className="p-4 bg-white rounded border border-slate-200"
    >
      <h2 className="text-lg font-medium text-slate-900 mb-3">
        Shortlist settings
      </h2>
      <div className="flex flex-wrap gap-4 items-end">
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-slate-600">Max investors</span>
          <input
            type="number"
            min={1}
            value={maxInvestors}
            onChange={(e) => setMaxInvestors(e.target.value)}
            className="border rounded px-2 py-1 w-32"
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-slate-600">Deadline hours</span>
          <input
            type="number"
            min={1}
            placeholder="48"
            value={deadlineHours}
            onChange={(e) => setDeadlineHours(e.target.value)}
            className="border rounded px-2 py-1 w-32"
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-slate-600">
            Absolute deadline (local time, optional)
          </span>
          <input
            type="datetime-local"
            value={absoluteDeadline}
            onChange={(e) => setAbsoluteDeadline(e.target.value)}
            className="border rounded px-2 py-1"
          />
        </label>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={onSave}
          className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  )
}

export default Settings
